import React from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/components';

const InformationRetentionPolicy: React.FC = () => {
  const sections = [
    { id: 'overview', title: '1. Overview' },
    { id: 'data-categories', title: '2. Data Categories and Retention Periods' },
    { id: 'audio-recordings', title: '3. Audio Recordings and Transcriptions' },
    { id: 'documents', title: '4. Generated Documents' },
    { id: 'account-data', title: '5. Account and Profile Data' },
    { id: 'usage-logs', title: '6. Usage Logs and Analytics' },
    { id: 'payment-data', title: '7. Payment and Billing Data' },
    { id: 'deletion-requests', title: '8. Data Deletion and Account Closure' },
    { id: 'backup-recovery', title: '9. Backups and Disaster Recovery' },
    { id: 'legal-holds', title: '10. Legal Holds and Exceptions' },
    { id: 'data-portability', title: '11. Data Portability and Export' },
    { id: 'changes', title: '12. Changes to This Policy' },
    { id: 'contact', title: '13. Contact Us' },
  ];

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="pt-20 pb-8 sm:pt-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4 leading-tight text-slate-900">
            Information Retention Policy
          </h1>
          <p className="text-lg text-slate-600">
            Last updated: February 12, 2026
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Table of Contents */}
            <div className="p-6 bg-slate-50 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Table of Contents</h2>
              <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-blue-600 hover:text-blue-800 text-sm hover:underline"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>

            {/* Policy Content */}
            <div className="p-6 sm:p-8 prose prose-slate max-w-none">
              <p className="text-slate-600 leading-relaxed mb-8">
                This Information Retention Policy describes how SystemDox collects, retains, and disposes
                of data processed through our platform. It supplements
                our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> and
                <Link to="/terms" className="text-blue-600 hover:underline"> Terms of Service</Link>. We
                are committed to retaining data only for as long as necessary to fulfil the purposes for
                which it was collected, comply with legal obligations, and support our legitimate business
                interests.
              </p>

              <section id="overview" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">1. Overview</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  SystemDox is an AI-powered documentation platform that processes audio recordings, text
                  input, and photographs to generate structured enterprise documentation. During the
                  course of providing this service, we process and store several categories of data on
                  your behalf.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  This policy applies to all data processed through the SystemDox platform, whether stored
                  in our cloud infrastructure (AWS) or temporarily cached on your device.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Our guiding principles for data retention are:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-3">
                  <li><strong>Purpose limitation:</strong> Data is retained only as long as it serves a defined purpose</li>
                  <li><strong>Data minimisation:</strong> We store the minimum data necessary to provide the service</li>
                  <li><strong>User control:</strong> You can delete your data at any time through your account settings</li>
                  <li><strong>Transparency:</strong> Retention periods are clearly documented for each data category</li>
                </ul>
              </section>

              <section id="data-categories" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">2. Data Categories and Retention Periods</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  The following table summarises the data categories we process and their retention periods:
                </p>
                <div className="overflow-x-auto mb-4">
                  <table className="min-w-full border border-slate-200 text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="border border-slate-200 px-4 py-3 text-left font-semibold text-slate-900">Data Category</th>
                        <th className="border border-slate-200 px-4 py-3 text-left font-semibold text-slate-900">Retention Period</th>
                        <th className="border border-slate-200 px-4 py-3 text-left font-semibold text-slate-900">Storage Location</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-600">
                      <tr>
                        <td className="border border-slate-200 px-4 py-3">Audio recordings</td>
                        <td className="border border-slate-200 px-4 py-3">Deleted after processing (within 24 hours)</td>
                        <td className="border border-slate-200 px-4 py-3">AWS S3 (encrypted)</td>
                      </tr>
                      <tr className="bg-slate-50/50">
                        <td className="border border-slate-200 px-4 py-3">Transcriptions</td>
                        <td className="border border-slate-200 px-4 py-3">Deleted after document generation (within 24 hours)</td>
                        <td className="border border-slate-200 px-4 py-3">AWS S3 (encrypted)</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-3">Generated documents</td>
                        <td className="border border-slate-200 px-4 py-3">Until deleted by user or account closure + 30 days</td>
                        <td className="border border-slate-200 px-4 py-3">AWS DynamoDB + S3</td>
                      </tr>
                      <tr className="bg-slate-50/50">
                        <td className="border border-slate-200 px-4 py-3">Project configuration</td>
                        <td className="border border-slate-200 px-4 py-3">Until deleted by user or account closure + 30 days</td>
                        <td className="border border-slate-200 px-4 py-3">AWS DynamoDB</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-3">User templates</td>
                        <td className="border border-slate-200 px-4 py-3">Until deleted by user or account closure + 30 days</td>
                        <td className="border border-slate-200 px-4 py-3">AWS DynamoDB</td>
                      </tr>
                      <tr className="bg-slate-50/50">
                        <td className="border border-slate-200 px-4 py-3">Account profile</td>
                        <td className="border border-slate-200 px-4 py-3">Until account closure + 30 days</td>
                        <td className="border border-slate-200 px-4 py-3">AWS Cognito + DynamoDB</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-3">Usage logs</td>
                        <td className="border border-slate-200 px-4 py-3">90 days</td>
                        <td className="border border-slate-200 px-4 py-3">AWS CloudWatch</td>
                      </tr>
                      <tr className="bg-slate-50/50">
                        <td className="border border-slate-200 px-4 py-3">Payment records</td>
                        <td className="border border-slate-200 px-4 py-3">7 years (legal requirement)</td>
                        <td className="border border-slate-200 px-4 py-3">Stripe + internal records</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-3">Error and performance data</td>
                        <td className="border border-slate-200 px-4 py-3">90 days</td>
                        <td className="border border-slate-200 px-4 py-3">Sentry</td>
                      </tr>
                      <tr className="bg-slate-50/50">
                        <td className="border border-slate-200 px-4 py-3">Photos and attachments</td>
                        <td className="border border-slate-200 px-4 py-3">Deleted after processing (within 24 hours)</td>
                        <td className="border border-slate-200 px-4 py-3">AWS S3 (encrypted)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="audio-recordings" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">3. Audio Recordings and Transcriptions</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Audio recordings are uploaded to AWS S3 with server-side encryption (AES-256) for the
                  sole purpose of transcription and document generation. We treat audio data as highly
                  sensitive and apply the following safeguards:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Ephemeral storage:</strong> Audio files are automatically deleted within 24 hours of
                    successful processing via S3 lifecycle policies</li>
                  <li><strong>No training use:</strong> Audio recordings and transcriptions are never used to train
                    AI models. We use AWS Transcribe which does not retain your audio data</li>
                  <li><strong>Access controls:</strong> Audio files are stored in user-scoped S3 prefixes with
                    IAM policies restricting access to the processing pipeline only</li>
                  <li><strong>Streaming transcription:</strong> When using real-time streaming mode, audio is
                    processed in memory and not persisted to disk</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  Transcription text is retained only as an intermediate processing step. Once the final
                  document is generated, the raw transcription is deleted. Only the generated document
                  (which you can review, edit, and delete) is retained.
                </p>
              </section>

              <section id="documents" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">4. Generated Documents</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Documents generated through SystemDox are your content. They are stored in AWS DynamoDB
                  (metadata) and S3 (full content) with the following policies:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Documents are retained for as long as your account is active</li>
                  <li>You can delete individual documents at any time from the Projects page</li>
                  <li>Deleted documents are permanently removed within 24 hours (no soft-delete or recycle bin)</li>
                  <li>Documents are encrypted at rest using AWS-managed encryption keys</li>
                  <li>Each document is scoped to your user ID and cannot be accessed by other users unless
                    you explicitly share it</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  Shared documents follow the same retention policy. If the owner deletes a shared document,
                  it is removed for all recipients. Revoking a share removes access immediately but does not
                  delete the document.
                </p>
              </section>

              <section id="account-data" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">5. Account and Profile Data</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We retain the following account information for the duration of your account:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Authentication credentials:</strong> Managed by AWS Cognito with industry-standard
                    password hashing (SRP protocol). Passwords are never stored in plaintext</li>
                  <li><strong>Profile information:</strong> Name, email address, organisation, and preferences</li>
                  <li><strong>User settings:</strong> Language preference, app mode, template categories, and
                    other personalisation settings</li>
                  <li><strong>Avatar:</strong> Profile images stored in S3 with user-scoped access</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  Upon account closure, all account data is scheduled for deletion within 30 days, subject
                  to any legal hold requirements (see Section 10).
                </p>
              </section>

              <section id="usage-logs" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">6. Usage Logs and Analytics</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We collect usage data to maintain, improve, and secure the Service:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Application logs:</strong> API request logs, error traces, and performance metrics
                    are retained in AWS CloudWatch for 90 days</li>
                  <li><strong>Error tracking:</strong> Crash reports and error data are retained in Sentry for
                    90 days. These may include anonymised stack traces and device information</li>
                  <li><strong>Processing job history:</strong> Records of document generation jobs (status, timestamps,
                    template used) are retained for the life of your account to support the job history feature</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  Usage logs are not used for advertising or sold to third parties. They are used solely for
                  service operation, debugging, and aggregate analytics.
                </p>
              </section>

              <section id="payment-data" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">7. Payment and Billing Data</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Payment processing is handled by Stripe. SystemDox does not store full payment card
                  details on our systems.
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Payment card data:</strong> Stored and managed entirely by Stripe in PCI DSS
                    Level 1 certified infrastructure</li>
                  <li><strong>Transaction records:</strong> Invoice and subscription data is retained for 7 years
                    to comply with UK tax and accounting regulations (HMRC requirements)</li>
                  <li><strong>Subscription status:</strong> Current plan, billing cycle, and subscription metadata
                    are retained for the life of your account</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  We retain only a reference to your Stripe customer ID and last four digits of your
                  payment method for display purposes.
                </p>
              </section>

              <section id="deletion-requests" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">8. Data Deletion and Account Closure</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  You can request deletion of your data at any time:
                </p>

                <h3 className="text-lg font-semibold text-slate-800 mb-3">Self-Service Deletion</h3>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Delete individual documents from the Projects page</li>
                  <li>Delete projects and their associated configuration</li>
                  <li>Delete custom templates from your template library</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-800 mb-3">Account Closure</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  To close your account and request full deletion of all associated data, contact us at
                  privacy@systemdox.com. Upon receiving a verified request:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li>Your account will be deactivated immediately</li>
                  <li>All user-generated content (documents, projects, templates) will be permanently deleted
                    within 30 days</li>
                  <li>Audio recordings and transcriptions (if any remain in processing) will be deleted
                    within 24 hours</li>
                  <li>Account profile and authentication data will be removed from Cognito within 30 days</li>
                  <li>Payment records will be retained for the legally required period (7 years) but
                    disassociated from your personal identity where possible</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-800 mb-3">Right to Erasure (GDPR)</h3>
                <p className="text-slate-600 leading-relaxed">
                  Under the UK GDPR and EU GDPR, you have the right to request erasure of your personal
                  data. We will honour erasure requests within 30 days, except where retention is required
                  by law (e.g., financial records) or necessary for the establishment, exercise, or defence
                  of legal claims.
                </p>
              </section>

              <section id="backup-recovery" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">9. Backups and Disaster Recovery</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  To ensure service reliability and data integrity, we maintain the following backup
                  practices:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>DynamoDB:</strong> Point-in-time recovery (PITR) is enabled, retaining continuous
                    backups for up to 35 days</li>
                  <li><strong>S3:</strong> Versioning is not enabled for user content buckets. Deleted objects
                    are permanently removed</li>
                  <li><strong>Infrastructure configuration:</strong> Stored as Infrastructure as Code (CloudFormation/SAM)
                    in version control, not containing user data</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  When you delete data, it is removed from the live database immediately. Backup copies
                  in PITR may persist for up to 35 days but are not accessible through the application
                  and are used solely for disaster recovery purposes.
                </p>
              </section>

              <section id="legal-holds" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">10. Legal Holds and Exceptions</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Standard retention periods may be overridden in the following circumstances:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Legal proceedings:</strong> Data may be preserved if required in connection with
                    litigation, regulatory investigation, or legal process</li>
                  <li><strong>Regulatory requirements:</strong> Certain data may be retained longer to comply with
                    applicable laws, including UK GDPR, the Data Protection Act 2018, and HMRC regulations</li>
                  <li><strong>Fraud prevention:</strong> Account data associated with suspected fraud or abuse
                    may be retained for up to 3 years after account closure</li>
                </ul>
                <p className="text-slate-600 leading-relaxed">
                  In all cases, we retain data only for as long as strictly necessary and apply appropriate
                  access controls to preserved data.
                </p>
              </section>

              <section id="data-portability" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">11. Data Portability and Export</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  You have the right to receive your data in a portable format:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Documents:</strong> All generated documents can be viewed and copied from the
                    Projects page. Markdown content is directly portable</li>
                  <li><strong>Projects:</strong> Project configuration and template assignments can be exported
                    through your account</li>
                  <li><strong>Full data export:</strong> To request a complete export of all your data, contact
                    us at privacy@systemdox.com. We will provide your data in a machine-readable format
                    (JSON) within 30 days</li>
                </ul>
              </section>

              <section id="changes" className="mb-10">
                <h2 className="text-xl font-bold text-slate-900 mb-4">12. Changes to This Policy</h2>
                <p className="text-slate-600 leading-relaxed">
                  We may update this Information Retention Policy from time to time to reflect changes in
                  our practices, technology, or legal requirements. We will notify you of any material
                  changes by posting the updated policy on this page and updating the "Last updated" date.
                  Where changes materially affect the retention of your data, we will provide notice via
                  email or through the Service.
                </p>
              </section>

              <section id="contact" className="mb-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">13. Contact Us</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  If you have any questions about this Information Retention Policy or wish to exercise
                  your data rights, please contact us:
                </p>
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <p className="text-slate-700 font-medium">SystemDox Data Protection</p>
                  <p className="text-slate-600">Email: privacy@systemdox.com</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default InformationRetentionPolicy;
